import React from "react";
import { Typography } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import { theme } from "../../theme";
import Avatar from "@mui/material/Avatar";
import ListItemText from "./list-item-text";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStethoscope } from "@fortawesome/free-solid-svg-icons";
import PropTypes from 'prop-types';

const PanelMedico = (props) => {
  const { medico, estilos, sisaData } = props;
  const classes = useStyles(theme);
  
  // Función auxiliar para manejar valores nulos o undefined
  const getValueOrDefault = (value, defaultValue = "No disponible") => {
    return value || defaultValue;
  };

  // Función para procesar el estado SISA
  const procesarEstadoSisa = (estado) => {
    const estadoNormalizado = (estado || '').toUpperCase();
    switch (estadoNormalizado) {
      case 'OK':
        return 'VERIFICADO';
      case 'NO_ENCONTRADO':
      case 'MÉDICO NO ENCONTRADO EN SISA':
        return 'NO_ENCONTRADO';
      case 'INHABILITADO':
        return 'INHABILITADO';
      case 'NO_ENCONTRADO_RECIPE':
        return 'NO_ENCONTRADO_RECIPE';
      default:
        return 'PENDIENTE';
    }
  };

  // Obtener datos de SISA de forma segura
  const sisaInfo = {
    codigoRefEps: getValueOrDefault(sisaData?.datos?.codigoRefEps),
    estadoSisaOriginal: getValueOrDefault(sisaData?.estado, "PENDIENTE"),
    matricula: getValueOrDefault(sisaData?.datos?.matricula),
    profesion: getValueOrDefault(sisaData?.datos?.profesion),
    debeVerificarSisa: sisaData?.datos?.debeVerificarSisa || false,
    permitirOperar: sisaData?.acciones?.permitirOperar !== false,
    bloquear: sisaData?.acciones?.bloquear || false,
    mostrarPopUp: sisaData?.acciones?.mostrarPopUpNuevamente || false,
    mensaje: getValueOrDefault(sisaData?.mensaje),
  };

  const estadoSisa = procesarEstadoSisa(sisaInfo.estadoSisaOriginal);
  const nombreCompleto = medico?.nombreMedico && medico?.apellidoMedico
    ? `${medico.nombreMedico} ${medico.apellidoMedico}`
    : "No disponible";

  // Asegurarnos de que los datos del médico existan
  const medicoData = {
    tipoMatricula: getValueOrDefault(medico?.tipoMatricula),
    nroMatricula: getValueOrDefault(medico?.nroMatricula),
  };
  
  return (
    <div className={classes.card}>
      <div
        className={classes.header}
        style={{ backgroundColor: estilos.backgroundColorAccent }}
      >
        <div className={classes.sectionName}>
          <Avatar variant="rounded" sx={{ height: 64, width: 64 }}>
            <FontAwesomeIcon icon={faStethoscope} size="xl" />
          </Avatar>
          <div className={classes.sectionText}>
            <Typography
              sx={{
                fontSize: "13.5px",
                lineHeight: "25px",
                color: estilos.colorTextPrimary,
              }}
            >
              Médico
            </Typography>
            <Typography
                sx={{
                  fontSize: "17.6px",
                  lineHeight: "25px",
                  color: estilos.colorTextSecondary,
                }}
              >
                {nombreCompleto}
              </Typography>
          </div>
        </div>
      </div>
      <div className={classes.body}>
        <ListItemText
          color={estilos.colorTextSecondary}
          label={"Tipo. de Matrícula"}
          data={medicoData.tipoMatricula}
        />
        <ListItemText
          color={estilos.colorTextSecondary}
          label={"Nro. de Matrícula"}
          data={medicoData.nroMatricula}
        />
        <ListItemText
          color={estilos.colorTextSecondary}
          label={"Código ReFEPS"}
          data={sisaInfo.codigoRefEps}
        />
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          alignSelf: 'flex-start',
          borderRadius: '4px',
          marginTop: '8px',
          minWidth: '200px'
        }}>
          <Typography
            sx={{
              fontSize: "14px",
              color: estilos.colorTextSecondary,
              fontWeight: 500,
              marginBottom: '4px'
            }}
          >
            Estado SISA
          </Typography>
          <div style={{
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ 
              backgroundColor: estadoSisa === 'VERIFICADO' ? '#e8f5e9' : '#ffebee',
              padding: '12px',
              borderRadius: '4px',
              border: `1px solid ${estadoSisa === 'VERIFICADO' ? '#66bb6a' : '#ef5350'}`,
              alignSelf: 'stretch'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {/* Estado principal */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: estadoSisa === 'VERIFICADO' ? '#66bb6a' : '#ef5350',
                    flexShrink: 0
                  }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: estadoSisa === 'VERIFICADO' ? '#2e7d32' : '#c62828',
                      fontWeight: 600
                    }}
                  >
                    {estadoSisa}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Definición de PropTypes para validación
PanelMedico.propTypes = {
  medico: PropTypes.shape({
    nombreMedico: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    apellidoMedico: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tipoMatricula: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nroMatricula: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  estilos: PropTypes.shape({
    backgroundColorAccent: PropTypes.string,
    colorTextPrimary: PropTypes.string,
    colorTextSecondary: PropTypes.string
  }).isRequired,
  sisaData: PropTypes.shape({
    estado: PropTypes.string,
    mensaje: PropTypes.string,
    datos: PropTypes.shape({
      codigoRefEps: PropTypes.string,
      matricula: PropTypes.string,
      profesion: PropTypes.string,
      debeVerificarSisa: PropTypes.bool,
      dniMasked: PropTypes.string,
      iniciales: PropTypes.string
    }),
    acciones: PropTypes.shape({
      permitirOperar: PropTypes.bool,
      bloquear: PropTypes.bool,
      mostrarPopUpNuevamente: PropTypes.bool
    })
  }),
  onClick: PropTypes.func
};

// Valores por defecto
PanelMedico.defaultProps = {
  medico: {},
  sisaData: null,
  onClick: () => {}
};

export default PanelMedico;

const useStyles = makeStyles((theme) => ({
  card: {
    backgroundColor: "white",
    borderRadius: 7,
    boxShadow: "0px 2px 1px -1px rgba(0, 0, 0, 0.2)",
    margin: "5px",
    border: "1px solid rgba(0, 0, 0, 0.14)",
    filter:
      "dropShadow(0px 1px 1px rgba(0, 0, 0, 0.14)) dropShadow(0px 1px 3px rgba(0, 0, 0, 0.12))",
  },
  header: {
    backgroundColor: "#E3E3E3",
    height: 100,
    padding: 15,
  },
  sectionSetting: {
    alignSelf: "flex-start",
    padding: 15,
  },
  sectionName: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: 0,
    gap: "16",
  },
  sectionText: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: 0,
    paddingLeft: "16px",
  },
  body: {
    display: "flex",
    padding: 15,
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 16,
  },
}));
